import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { ProvisioningRequest, RequestStatus } from '@/types';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge } from '@/components/StatusBadge';
import { formatDate } from '@/lib/utils';
import { Search, Loader2, FileText, RefreshCw, Users } from 'lucide-react';

export function AdminDashboard() {
  const [requests, setRequests] = useState<ProvisioningRequest[]>([]);
  const [stats, setStats] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('Submitted');
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadRequests();
    loadStats();
  }, [statusFilter]);

  const loadRequests = async () => {
    setIsLoading(true);
    try {
      const filter = statusFilter === 'all' ? undefined : statusFilter;
      const { requests } = await api.getAdminRequests(filter, search || undefined);
      setRequests(requests);
    } catch (error) {
      console.error('Failed to load requests:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const { stats } = await api.getStats();
      setStats(stats);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleSearch = () => {
    loadRequests();
  };

  const statuses: RequestStatus[] = [
    'Draft', 'Submitted', 'Approved', 'Provisioning',
    'Provisioned', 'Failed', 'Completed', 'Rejected'
  ];

  const statCards = [
    { status: 'Submitted', label: 'Pending Review', color: 'bg-blue-500' },
    { status: 'Provisioning', label: 'In Progress', color: 'bg-yellow-500' },
    { status: 'Failed', label: 'Failed', color: 'bg-red-500' },
    { status: 'Provisioned', label: 'Awaiting Completion', color: 'bg-green-500' },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-gray-600 mt-1">Manage provisioning requests</p>
          </div>
          <div className="flex gap-2">
            <Link to="/admin/users">
              <Button variant="outline">
                <Users className="h-4 w-4 mr-2" />
                Manage Users
              </Button>
            </Link>
            <Button variant="outline" onClick={() => { loadRequests(); loadStats(); }}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {statCards.map((card) => (
            <Card
              key={card.status}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setStatusFilter(card.status)}
            >
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">{card.label}</p>
                    <p className="text-3xl font-bold">{stats[card.status] || 0}</p>
                  </div>
                  <div className={`h-12 w-12 rounded-full ${card.color} opacity-20`} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Requests Table */}
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle>Provisioning Requests</CardTitle>
                <CardDescription>
                  {requests.length} request{requests.length !== 1 ? 's' : ''} found
                </CardDescription>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    placeholder="Search..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="pl-9 w-[200px]"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    {statuses.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-abby-primary" />
              </div>
            ) : requests.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <FileText className="h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-600">No requests found</p>
              </div>
            ) : (
              <>
                {/* Mobile/tablet card view */}
                <div className="lg:hidden space-y-4">
                  {requests.map((request) => (
                    <div key={request.id} className="border rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-medium text-gray-900">
                            {request.payload.Account_Name}
                          </p>
                          <p className="text-sm text-gray-500">
                            {request.payload.Industry}
                          </p>
                        </div>
                        <StatusBadge status={request.status} />
                      </div>
                      {request.last_error && (
                        <p className="text-xs text-red-600 mb-3 break-words">
                          {request.last_error}
                        </p>
                      )}
                      <div className="space-y-2 text-sm mb-3">
                        <div>
                          <p className="text-gray-500">Contact</p>
                          <p className="text-gray-900">
                            {request.payload.First_Name} {request.payload.Last_Name}
                          </p>
                          <p className="text-gray-500">{request.payload.Email}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <p className="text-gray-500">Reseller</p>
                            <p className="text-gray-900 font-mono text-xs break-all">
                              {request.reseller_sf_id}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500">Attempts</p>
                            <p className="text-gray-900">{request.attempt_count}</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-500">
                          Updated {formatDate(request.updated_at)}
                        </p>
                        <Link to={`/admin/requests/${request.id}`}>
                          <Button variant="ghost" size="sm">
                            View
                          </Button>
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop table view */}
                <div className="hidden lg:block">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left text-sm text-gray-500">
                        <th className="pb-3 font-medium">Account</th>
                        <th className="pb-3 font-medium">Contact</th>
                        <th className="pb-3 font-medium">Reseller</th>
                        <th className="pb-3 font-medium">Status</th>
                        <th className="pb-3 font-medium">Attempts</th>
                        <th className="pb-3 font-medium">Updated</th>
                        <th className="pb-3 font-medium"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {requests.map((request) => (
                        <tr key={request.id} className="hover:bg-gray-50">
                          <td className="py-4">
                            <div>
                              <p className="font-medium text-gray-900">
                                {request.payload.Account_Name}
                              </p>
                              <p className="text-sm text-gray-500">
                                {request.payload.Industry}
                              </p>
                            </div>
                          </td>
                          <td className="py-4">
                            <div>
                              <p className="text-gray-900">
                                {request.payload.First_Name} {request.payload.Last_Name}
                              </p>
                              <p className="text-sm text-gray-500">
                                {request.payload.Email}
                              </p>
                            </div>
                          </td>
                          <td className="py-4">
                            <p className="text-sm text-gray-500 font-mono">
                              {request.reseller_sf_id}
                            </p>
                          </td>
                          <td className="py-4">
                            <StatusBadge status={request.status} />
                            {request.last_error && (
                              <p className="text-xs text-red-600 mt-1 max-w-[200px] truncate" title={request.last_error}>
                                {request.last_error}
                              </p>
                            )}
                          </td>
                          <td className="py-4 text-sm text-gray-500">
                            {request.attempt_count}
                          </td>
                          <td className="py-4 text-sm text-gray-500">
                            {formatDate(request.updated_at)}
                          </td>
                          <td className="py-4">
                            <Link to={`/admin/requests/${request.id}`}>
                              <Button variant="ghost" size="sm">
                                View
                              </Button>
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
